import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import Icon from '../AppIcon';
import Button from './Button';

/**
 * ThemeToggle Component
 * Provides a dropdown menu to switch between Light, Dark, and System themes
 */
const ThemeToggle = () => {
  const { theme, setTheme, effectiveTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const themeOptions = [
    { value: 'light', label: 'Light', icon: 'Sun', description: 'Light theme' },
    { value: 'dark', label: 'Dark', icon: 'Moon', description: 'Dark theme' },
    { value: 'system', label: 'System', icon: 'Monitor', description: 'Follow system preference' }
  ];

  const getCurrentIcon = () => {
    if (theme === 'system') return 'Monitor';
    return effectiveTheme === 'dark' ? 'Moon' : 'Sun';
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Theme Toggle Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title={`Current theme: ${theme}`}
      >
        <Icon 
          name={getCurrentIcon()} 
          size={18} 
          className="text-gray-600 dark:text-gray-300" 
        />
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Theme
            </p>
          </div>
          
          {themeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleThemeChange(option.value)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                theme === option.value
                  ? 'bg-primary/10 text-primary dark:bg-primary/20'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                theme === option.value
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}>
                <Icon name={option.icon} size={16} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{option.label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {option.description}
                </p>
              </div>
              {theme === option.value && (
                <Icon name="Check" size={16} className="text-primary" />
              )}
            </button>
          ))}
          
          {/* Current effective theme indicator */}
          <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Active: <span className="font-medium capitalize">{effectiveTheme}</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeToggle;
